<script setup>
import { computed, useId } from 'vue';
import { cva } from 'class-variance-authority';
import { useAsyncData } from '@/composables/useAsyncData';
import { fetchSurahs } from '@/utils/quran/api';
import { artworkGradient } from '@/utils/quran/artwork';

/**
 * Cover art for a surah, like an album cover: its Arabic name in gold over an
 * eight-pointed-star lattice, on one of a few deep brand gradients chosen by
 * the surah number — so each surah keeps the same face everywhere.
 */
const props = defineProps({
  surah: { type: Number, required: true },
  size: { type: String, default: 'md' },
  label: { type: Boolean, default: false },
});

const patternId = `art-${useId()}`;
const { data: surahs } = useAsyncData(() => 'all', fetchSurahs);
const info = computed(() => surahs.value?.find((s) => s.id === props.surah) ?? null);
const gradient = computed(() => artworkGradient(props.surah));

const frame = cva('relative overflow-hidden shrink-0 [container-type:inline-size] shadow-[inset_0_0_0_1px_rgba(236,215,159,0.18)]', {
  variants: {
    size: {
      xs: 'w-11 h-11 rounded-xl',
      sm: 'w-14 h-14 rounded-2xl',
      md: 'w-40 h-40 rounded-3xl',
      lg: 'w-full aspect-square rounded-[28px] shadow-[0_40px_90px_-30px_rgba(0,0,0,0.65)]',
    },
  },
});
</script>

<template>
  <div
    :class="frame({ size })"
    :style="{ background: `radial-gradient(120% 90% at 30% 15%, ${gradient[0]} 0%, ${gradient[1]} 100%)` }"
    role="img"
    :aria-label="info ? `${info.transliteration} · ${info.name_arabic}` : `${surah}`"
  >
    <svg class="absolute inset-0 w-full h-full opacity-[0.16]" aria-hidden="true">
      <defs>
        <pattern :id="patternId" width="36" height="36" patternUnits="userSpaceOnUse">
          <path
            d="M18 1.5l4.1 5.7 7-1.1-1.1 7 5.7 4.1-5.7 4.1 1.1 7-7-1.1L18 34.5l-4.1-5.7-7 1.1 1.1-7L2.3 18.8l5.7-4.1-1.1-7 7 1.1z"
            fill="none"
            stroke="#ecd79f"
            stroke-width="0.8"
          />
        </pattern>
      </defs>
      <rect width="100%" height="100%" :fill="`url(#${patternId})`" />
    </svg>
    <div class="absolute -top-1/4 -right-1/4 w-3/4 h-3/4 rounded-full bg-deen-gold/20 blur-2xl" aria-hidden="true" />

    <div class="absolute inset-0 flex items-center justify-center p-[8%]">
      <span
        v-if="info"
        dir="rtl"
        lang="ar"
        class="font-quran text-deen-gold leading-[1.6] text-center [text-shadow:0_2px_24px_rgba(0,0,0,0.35)]"
        :class="info.name_arabic.length > 8 ? 'text-[19cqw]' : 'text-[26cqw]'"
      >{{ info.name_arabic }}</span>
    </div>

    <div v-if="label && info" class="absolute inset-x-0 bottom-0 p-[6%] flex items-end justify-between gap-2 text-white">
      <span class="text-[4.2cqw] font-semibold tracking-wide">{{ info.transliteration }}</span>
      <span class="text-[3.6cqw] font-bold uppercase tracking-widest text-deen-gold/80">{{ surah }} · {{ info.ayah_count }}</span>
    </div>
  </div>
</template>
